import { Stack } from '@mui/material';
import * as React from 'react';
import { useState } from 'react';
import {
    Identifier,
    useGetMany,
    useRecordContext,
    useRefresh,
} from 'react-admin';
import { TagChip } from '../../tags/TagChip';
import { TagCreateModal } from '../../tags/TagCreateModal';
import { MemberLight, MemberSummary, Tag, updateMemberTags } from '@wwf/sdk';
import { TagSearchButtonWithModal } from './TagSearchButtonWithModal';

export const TagsListEdit = () => {
    const record = useRecordContext<MemberSummary | MemberLight>();
    const [createOpen, setCreateOpen] = useState(false);
    const { data: tags, isPending: isPendingRecordTags } = useGetMany<Tag>(
        'tags',
        { ids: record?.tags ?? [] },
        {
            enabled:
                !!record &&
                Array.isArray(record.tags) &&
                record.tags.length > 0,
        }
    );
    const refresh = useRefresh();

    const handleTagAdd = async (id: Tag['id']) => {
        if (!record) {
            throw new Error('No member record found');
        }
        const tags = [...(record.tags || []), id];
        await updateMemberTags(record.id, { tags });
        refresh();
    };

    const handleTagDelete = async (id: Identifier) => {
        if (!record) {
            throw new Error('No member record found');
        }
        const tags = record.tags?.filter(tagId => tagId !== id) || [];
        await updateMemberTags(record.id, { tags });
        refresh();
    };

    const openTagCreateDialog = () => {
        setCreateOpen(true);
    };

    const handleTagCreateClose = () => {
        setCreateOpen(false);
    };

    const handleTagCreated = React.useCallback(
        async (tag: Tag) => {
            if (!record) {
                throw new Error('No member record found');
            }

            await updateMemberTags(record.id, {
                tags: [...(record.tags || []), tag.id],
            });
            refresh();
            setCreateOpen(false);
        },
        [record, refresh]
    );

    if (isPendingRecordTags) return null;
    return (
        <>
            <Stack flexWrap="wrap" direction="row" mt={1} mb={1} gap={1}>
                {tags?.map(tag => (
                    <TagChip
                        tag={tag}
                        onUnlink={() => handleTagDelete(tag.id)}
                        key={tag.id}
                    />
                ))}
            </Stack>
            <TagCreateModal
                open={createOpen}
                onClose={handleTagCreateClose}
                onSuccess={handleTagCreated}
            />
            <TagSearchButtonWithModal
                openTagCreateDialog={openTagCreateDialog}
                selectedTags={record?.tags || []}
                onTagAdd={handleTagAdd}
            />
        </>
    );
};
